import { Flex, Heading, Text, toast } from '@sparrowengg/twigs-react';
import CodeIcon from 'commons/icons/code';
import CloudIcon from 'commons/icons/cloud';
import DesktopIcon from 'commons/icons/desktop';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { devPortalServices } from 'commons/services';
import { defaultValues } from 'features/apps/add-local-dev-app/constants';

const ConfigPage = () => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const handleConfigureLocalDevApp = async () => {
    setCreating(true);
    try {
      // Prepare form data with default values
      const formData = new FormData();

      // Convert video URL if needed
      const videoUrl =
        defaultValues.videoUrl?.trim() ||
        'https://www.youtube.com/watch?v=RymrDTbydWc';
      const convertUrl = (url: string): string => {
        const youtubeRegex =
          /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(.{0,500})$/;
        if (youtubeRegex.test(url)) {
          const videoIdMatch = url.match(youtubeRegex);
          const videoId = videoIdMatch ? videoIdMatch[4] : null;
          if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;
          }
        }
        return url;
      };

      formData.append('videoUrl', convertUrl(videoUrl));
      formData.append('appName', defaultValues.appName || 'untitled');
      formData.append(
        'shortDescription',
        defaultValues.shortDescription || 'new app'
      );
      formData.append(
        'appOverView',
        defaultValues.appOverView || 'local dev app'
      );
      formData.append(
        'appInstructions',
        defaultValues.appInstructions || 'new app'
      );
      formData.append(
        'supportEmail',
        defaultValues.supportEmail || 'support@gmail.com'
      );
      formData.append('supportUrl', defaultValues.supportUrl || '');
      formData.append('websiteLink', defaultValues.websiteLink || '');
      formData.append(
        'ngrokUrl',
        defaultValues.ngrokUrl || 'https://a1b2c3d4e5f6.ngrok-free.app'
      );

      // Handle events
      if (
        Array.isArray(defaultValues.events) &&
        defaultValues.events.length > 0
      ) {
        defaultValues.events.forEach((event: string) => {
          formData.append('events[]', event);
        });
      }

      // Handle location
      if (defaultValues.location) {
        formData.append('locationName', defaultValues.location);
      }

      // Handle iParams (only if not empty)
      if (
        defaultValues.iParams &&
        typeof defaultValues.iParams === 'object' &&
        Object.keys(defaultValues.iParams).length > 0
      ) {
        formData.append('iParams', JSON.stringify(defaultValues.iParams));
      }

      // Handle oauth (only if not empty)
      if (
        defaultValues.oauth &&
        typeof defaultValues.oauth === 'object' &&
        Object.keys(defaultValues.oauth).length > 0
      ) {
        formData.append('oauth', JSON.stringify(defaultValues.oauth));
      }

      // Handle whitelistedDomains - ensure it's a simple array of trimmed strings
      if (
        Array.isArray(defaultValues.whitelistedDomains) &&
        defaultValues.whitelistedDomains.length > 0
      ) {
        // Filter out empty strings, trim each domain, and remove duplicates
        const processedDomains = defaultValues.whitelistedDomains
          .map((domain: any) => {
            // If domain is a stringified array, parse it
            if (typeof domain === 'string' && domain.trim().startsWith('[')) {
              try {
                const parsed = JSON.parse(domain);
                return Array.isArray(parsed) ? parsed : [domain];
              } catch {
                return domain.trim();
              }
            }
            return typeof domain === 'string'
              ? domain.trim()
              : String(domain).trim();
          })
          .flat() // Flatten in case we had nested arrays
          .filter((domain: string) => domain.length > 0) // Remove empty strings
          .filter(
            (domain: string, index: number, self: string[]) =>
              self.indexOf(domain) === index
          ); // Remove duplicates

        // Append each domain as a separate entry (not stringified)
        processedDomains.forEach((domain: string) => {
          formData.append('whitelistedDomains[]', domain);
        });
      }

      // Create the app
      const response = await devPortalServices.uploadLocalDevApp(formData);

      if (response?.appId) {
        // Create app update request with isNewApp: true
        // const appUpdateRequestPayload = {
        //   payload: {},
        //   appId: response.appId,
        //   isNewApp: true
        // };
        // await devPortalServices.createAppUpdateRequest(appUpdateRequestPayload);

        // Navigate directly to edit page
        navigate(`/edit-local-dev-app/${response.appId}`);
      } else {
        toast({
          variant: 'error',
          description: 'Failed to create app. Please try again.'
        });
      }
    } catch (err) {
      console.error('Error creating local dev app:', err);
      toast({
        variant: 'error',
        description: 'Error creating app. Please try again.'
      });
    } finally {
      setCreating(false);
    }
  };
  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      css={{ marginTop: 108 }}
    >
      <Flex flexDirection="column" gap="$2" alignItems="center">
        <Heading size="h5">Build or Deploy?</Heading>
        <Text size="md" css={{ color: '$neutral800' }}>
          Choose either of the two options to build your AppNest application
        </Text>
      </Flex>
      <Flex css={{ marginTop: '$20' }} gap="$12">
        <ConfigCard
          icon={<CloudIcon />}
          heading="Publish App"
          onClickHandler={() => navigate('/add-apps')}
          description="Upload the .zip file directly, fill the required information and deploy the app."
        />
        <ConfigCard
          icon={<CodeIcon />}
          heading="Start from scratch"
          onClickHandler={() => navigate('/code-editor')}
          description="Quickly build the entire application from scratch within the cloud."
        />
        {/* <ConfigCard
          icon={<DesktopIcon />}
          heading="Configure Local Dev App"
          onClickHandler={handleConfigureLocalDevApp}
          description="Configure your local development app with manifest file and ngrok URL for testing."
          loading={creating}
        /> */}
      </Flex>
    </Flex>
  );
};

export default ConfigPage;

const ConfigCard = ({
  icon,
  heading,
  description,
  onClickHandler,
  loading
}: {
  icon: React.ReactNode;
  heading: string;
  description: string;
  onClickHandler: () => void;
  loading?: boolean;
}) => {
  return (
    <Flex
      onClick={loading ? undefined : onClickHandler}
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      css={{
        border: '$borderWidths$xs solid $neutral200',
        borderRadius: '$3xl',
        width: 388,
        height: 244,
        padding: '$24',
        cursor: loading ? 'wait' : 'pointer',
        transition: 'all .5s ease',
        textAlign: 'center',
        opacity: loading ? 0.6 : 1,
        '&:hover': {
          background: loading ? 'transparent' : '#F3F9FA',
          borderColor: loading ? '$neutral200' : '$primary300'
        }
      }}
    >
      {icon}
      <Flex css={{ marginTop: '$12' }} flexDirection="column" gap="$4">
        <Heading size="h6">{heading}</Heading>
        <Text size="sm" css={{ color: '$black900' }}>
          {description}
        </Text>
      </Flex>
    </Flex>
  );
};
